import React, { useMemo, MouseEventHandler } from "react";
import { twMerge } from "tailwind-merge";

import { SupText as Text } from "../../../components/UI/Text";
import { toPixels } from "../../../utils/css";
import { CommonColors } from "../../../types/colors";

interface ProgressProps {
  value: number;
  color?: CommonColors | "current";
  small?: boolean;
  className?: string;
  minValue?: number;
  maxValue?: number;
  suffix?: string;
  onClick?: MouseEventHandler<HTMLSpanElement>;
}

const CIRCLE_PARAMS = {
  radius: 10,
  strokeWidth: 1,
};

const SMALL_CIRCLE_PARAMS = {
  radius: 5,
  strokeWidth: 1,
};

const COLORS = {
  current: "text-current",
  primary: "text-neutral-900",
  success: "text-success-800",
  error: "text-error-700",
  info: "text-info-700",
  warning: "text-warning-600",
} as const;

export function SupProgress({ value, className, color, small = false, minValue = 0, maxValue = 100, suffix = "", onClick }: ProgressProps) {
  const { radius, viewBox, x, y, circumference } = useMemo(() => {
    const { radius, strokeWidth } = small ? SMALL_CIRCLE_PARAMS : CIRCLE_PARAMS;
    const radiusWithStroke = radius + strokeWidth;

    return {
      radius,
      viewBox: [0, 0, 2 * radiusWithStroke, 2 * radiusWithStroke].join(" "),
      x: radiusWithStroke,
      y: radiusWithStroke,
      circumference: 2 * radius * Math.PI,
    };
  }, [small]);
  const formattedValue = useMemo(() => Math.min(maxValue, Math.max(minValue, Math.round(value))), [value, maxValue, minValue]);
  const strokeDashoffset = useMemo(() => circumference - (formattedValue * circumference) / maxValue, [circumference, formattedValue, maxValue]);

  return (
    <span className={twMerge("sup-progress-circle inline-flex", color && COLORS[color], small ? "h-3 w-3" : "h-5 w-5", className)} onClick={onClick}>
      <svg viewBox={viewBox} className="-m-px">
        <circle
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          style={{ strokeDasharray: toPixels(circumference), strokeDashoffset: toPixels(strokeDashoffset), transform: `translate(${x}px, ${y}px) rotate(-90deg)` }}
        />
        {!small && (
          <Text
            variant="detail"
            weight="bold"
            as="text"
            style={{ fill: "currentColor", transform: `translate(${x}px, ${y + 1}px)`, textAnchor: "middle", alignmentBaseline: "middle" }}
          >
            {formattedValue}
            {suffix}
          </Text>
        )}
      </svg>
    </span>
  );
}
